import { useRef, useCallback, useEffect, useState, useImperativeHandle, forwardRef } from 'react';
import { Table, Button, Space } from 'antd';
import PropTypes from 'prop-types';
import XForm from 'components/XForm';
import './style.less';
const XTable = (props, xTable) => {
    const {
        queryItems = [],
        api,
        apiParams = {},
        pageSize = 10,
        pageSizeOptions = [10, 20, 50, 100],
        scrollY,
        actionBtns = [],
        tableData,
        dataTotalKeys = [],
        dataTotalObj,
        columns,
        handelParams, // 处理请求parms
        ...antdTableApis // antd-table 自身api
    } = props;
    const xFormRef = useRef();
    const [data, setData] = useState([]);
    const [tableLoading, setTableLoading] = useState(false);
    const [total, setTotal] = useState(0);
    const tablSelfParams = useRef({}); // 列表onChange的参数（排序、筛选、分页选择）

    // 表格刷新
    const refreshTable = useCallback(
        (dataSubOne = false) => {
            // dataSubOne = true 数据少一条 判断是否page-1
            const { page = 1, size = 10 } = tablSelfParams.current;
            if (dataSubOne && data.length % size === 1 && page > 1) {
                tablSelfParams.current = { ...tablSelfParams.current, page: page - 1 };
            }
            getData();
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [data],
    );

    useImperativeHandle(
        xTable,
        () => ({
            // 暴露给父组件的方法
            queryForm: xFormRef.current?.form,
            refreshTable: refreshTable,
        }),
        [refreshTable],
    );

    // 初始化触发查询
    useEffect(() => {
        if (tableData) return;
        getData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // 获取queryItems的搜索值
    const getQueryParams = () => {
        let queryParams = {};
        if (queryItems.length) {
            const values = xFormRef.current.form?.getFieldsValue() || {};
            const queryKeys = Object.keys(values);
            queryKeys.forEach(key => {
                const value = values[key];
                if (value) {
                    queryParams = { ...queryParams, [key]: value };
                }
            });
        }
        return queryParams;
    };

    // 请求数据
    const getData = () => {
        setTableLoading(true);
        const queryParams = getQueryParams();
        let params = {
            page: 1,
            size: pageSize,
            ...(tablSelfParams.current || {}),
            ...queryParams,
            ...apiParams,
        };
        if (handelParams) {
            params = handelParams(params);
        }
        api(params).then(res => {
            let list = [];
            let total = 0;
            if (res.success) {
                list = res.list || [];
                total = res.total || 0;
            }
            setData(list);
            setTotal(total);
            setTableLoading(false);
        });
    };

    // 点击查询
    const handelQuery = () => {
        tablSelfParams.current.page = 1;
        getData();
    };

    // 列表触发查询
    const onTableChange = (pagination, filters, sorter) => {
        if (tableData) return;
        // 添加分页参数
        let params = {
            page: pagination.current || 1,
            size: pagination.pageSize || 10,
        };
        // 添加排序参数
        if (sorter.order && sorter.field) {
            params = {
                ...params,
                field: sorter.field,
                order: { ascend: 'asc', descend: 'desc' }[sorter.order],
            };
        }
        // 添加筛选参数
        const filterKeys = Object.keys(filters);
        if (filterKeys.length) {
            filterKeys.forEach(key => {
                const filterVal = filters[key];
                if (filterVal) {
                    const filterMultiple =
                        (props.columns || []).find(item => {
                            return item.dataIndex === key;
                        })?.filterMultiple ?? true; // 默认多选
                    params = {
                        ...params,
                        [key]: !filterMultiple ? filterVal[0] : filterVal,
                    };
                }
            });
        }
        tablSelfParams.current = params;
        getData();
    };

    const formItems = useCallback(() => {
        return [
            ...queryItems,
            {
                item: (
                    <Button type="primary" htmlType="submit">
                        查询
                    </Button>
                ),
            },
            {
                item: <Button onClick={() => xFormRef.current.form?.resetFields()}>重置</Button>,
            },
        ];
    }, [queryItems]);

    const tablTitle = () => {
        return (
            <Space size={[20, 10]} wrap>
                {actionBtns.map((btn, inedx) => {
                    const { text, ...btnApis } = btn;
                    return (
                        <Button key={`btn_${inedx}`} type="primary" {...btnApis}>
                            {text}
                        </Button>
                    );
                })}
            </Space>
        );
    };

    return (
        <div className="XTableMain">
            {queryItems.length ? (
                <div className="queryBox">
                    <XForm
                        formApis={{
                            layout: 'inline',
                            onFinish: handelQuery,
                        }}
                        formItems={formItems()}
                        ref={xFormRef}
                    />
                </div>
            ) : null}
            <Table
                dataSource={tableData || data}
                columns={columns}
                bordered
                loading={tableLoading}
                pagination={
                    antdTableApis.pagination === false
                        ? false
                        : {
                              pageSizeOptions,
                              hideOnSinglePage: false,
                              showSizeChanger: true,
                              showQuickJumper: true,
                              showTotal: totals => `共 ${totals} 项`,
                              position: ['bottomCenter'],
                              total,
                              current: tablSelfParams.current?.page || 1,
                              pageSize: tablSelfParams.current?.size || pageSize,
                              ...(typeof antdTableApis.pagination === 'object'
                                  ? antdTableApis.pagination
                                  : {}),
                          }
                }
                scroll={{ scrollToFirstRowOnChange: true, x: 'max-content', y: scrollY }}
                onChange={onTableChange}
                title={actionBtns.length ? tablTitle : ''}
                {...antdTableApis}
                // 合计行
                summary={pageData => {
                    if (!dataTotalObj || !dataTotalKeys.length || !pageData.length) return null;
                    const totals = {};
                    pageData.forEach(item => {
                        dataTotalKeys.forEach(key => {
                            const keyVal = Number(((item[key] || 0) + '').replace(',', ''));
                            totals[key] = Number(totals[key] || 0) + keyVal;
                        });
                    });
                    const totalData = { ...dataTotalObj(totals) };
                    return (
                        <>
                            <Table.Summary.Row>
                                {columns.map((column, index) => {
                                    return (
                                        <Table.Summary.Cell key={column.dataIndex + index}>
                                            {totalData[column.dataIndex]}
                                        </Table.Summary.Cell>
                                    );
                                })}
                            </Table.Summary.Row>
                        </>
                    );
                }}
            />
        </div>
    );
};
export default forwardRef(XTable);

XTable.propTypes = {
    queryItems: PropTypes.array, // 搜索项
    api: PropTypes.func, // 请求数据方法
    apiParams: PropTypes.object, // 接口携带参数
    pageSize: PropTypes.number, // 默认分页大小 默认10
    pageSizeOptions: PropTypes.array, // 分页器 默认[10, 20, 50, 100]
    scrollY: PropTypes.oneOfType([PropTypes.string, PropTypes.number]), // 表格纵向滚动
    actionBtns: [], // 操作栏按钮配置
};
/*
使用示例：columns api rowKey必传
    可利用xTableRef调用queryForm获得form实例，可调用form方法 例：xTableRef.current.queryForm?.getFieldsValue()
    const xTableRef = useRef();
    const columns = [
        {
            title: '',
            dataIndex: '',
            render: val => (val),
            filters: filters,
            filterMultiple: false,
            sorter: true,
        }
    ]
    // const actionBtns = [{antd-button的api, text: '按钮名称必传'}]
    <XTable
        columns={columns} 
        queryItems={queryItems} 
        api={api} 
        rowKey="id" 
        ref={xTableRef}
    />
*/
